import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KheUoc,
  Phi,
  VatTu,
  VuViec
} from '@/types/schemas';
import type { DataTableCol, DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { buildInputTableCols } from '@/components/custom/erp/input-table-v2';
import { CellFieldV2, SearchFieldV2 } from '@/components/custom/erp';
import type { FormMode } from '@/types/form';

type DetailDataTableColumn = DataTableColumn & { pasteConfig?: MuaHangRecord; nonWritable?: boolean };
type DetailInputColumn = DataTableCol<MuaHangRecord> & { pasteConfig?: MuaHangRecord };

const buildColumnsWithPasteConfig = (columns: DetailInputColumn[]): DetailDataTableColumn[] =>
  buildInputTableCols<MuaHangRecord>(columns).map((column, index) => ({
    ...column,
    pasteConfig: columns[index]?.pasteConfig
  }));

export const getDetailTableColumns = (
  formMode: FormMode,
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void
): DetailDataTableColumn[] =>
  buildColumnsWithPasteConfig([
    {
      field: 'tk_co',
      headerName: 'TK có',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<AccountModel>
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_co_data?.code || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_co_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'code',
        resultField: 'tk_co_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'ten_tk',
      headerName: 'Tên tài khoản',
      width: 200,
      renderCell: params => (
        <CellFieldV2
          commitOnBlur
          name='ten_tk'
          type='text'
          value={params.row.tk_co_data?.name || ''}
          disabled={formMode === 'view'}
        />
      ),
      nonWritable: true
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 200,
      renderCell: params => (
        <CellFieldV2
          commitOnBlur
          name='dien_giai'
          type='text'
          value={params.row.dien_giai}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
          disabled={formMode === 'view'}
        />
      )
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 100,
      renderCell: params => (
        <CellFieldV2
          commitOnBlur
          name='so_luong'
          type='number'
          placeholder='0'
          value={params.row.so_luong}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
          disabled={formMode === 'view'}
        />
      )
    },
    {
      field: 'gia_nt',
      headerName: 'Giá VND',
      width: 175,
      renderCell: params => (
        <CellFieldV2
          commitOnBlur
          name='gia_nt'
          type='number'
          placeholder='0'
          value={params.row.gia_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
          disabled={formMode === 'view'}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 175,
      renderCell: params => (
        <CellFieldV2
          commitOnBlur
          name='tien_nt'
          type='number'
          value={params.row.tien_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={formMode === 'view'}
        />
      )
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<BoPhan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
          searchColumns={boPhanSearchColumns}
          columnDisplay='ma_bp'
          dialogTitle='Danh mục bộ phận'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<VuViec>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
          searchColumns={vuViecSearchColumns}
          columnDisplay='ma_vu_viec'
          dialogTitle='Danh mục vụ việc'
          value={params.row.ma_vv_data?.ma_vu_viec || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vu_viec',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<HopDong>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
          searchColumns={hopDongSearchColumns}
          columnDisplay='ma_hd'
          dialogTitle='Danh mục hợp đồng'
          value={params.row.ma_hd_data?.ma_hd || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_hd',
        resultField: 'ma_hd_data',
        detailEndpoint: QUERY_KEYS.HOP_DONG
      }
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<DotThanhToan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
          searchColumns={dotThanhToanSearchColumns}
          columnDisplay='ma_dtt'
          dialogTitle='Danh mục đợt thanh toán'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<KheUoc>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
          searchColumns={kheUocSearchColumns}
          columnDisplay='ma_ku'
          dialogTitle='Danh mục khế ước'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<Phi>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}/`}
          searchColumns={phiSearchColumns}
          columnDisplay='ma_phi'
          dialogTitle='Danh mục phí'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục sản phẩm'
          value={params.row.ma_sp_data?.ma_vt || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_sp_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<MuaHangRecord>
          searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
          searchColumns={lenhSanXuatSearchColumns}
          columnDisplay='ma_lsx'
          dialogTitle='Lệnh sản xuất'
          value={params.row.ma_lsx_data?.ma_lsx || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_lsx',
        resultField: 'ma_lsx_data',
        detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
      }
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 150,
      renderCell: params => (
        <SearchFieldV2<ChiPhiKhongHopLeData>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          columnDisplay='ma_cp_khl'
          dialogTitle='Danh mục chi phí không hợp lệ'
          value={params.row.ma_cp0_data?.ma_cpkhl || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
          className='w-full'
          disabled={formMode === 'view'}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cp_khl',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    }
  ]);
